import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/feature/PageHeader";
import JsonLd from "@/components/feature/JsonLd";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { reportNotes, reportScopes, reportSteps } from "@/mocks/site";
import ReportForm from "./components/ReportForm";

const timingChips = [
  { icon: "ri-timer-flash-line", label: "初步受理", value: "24 小时内" },
  { icon: "ri-search-eye-line", label: "核查周期", value: "3–5 个工作日" },
  { icon: "ri-notification-3-line", label: "结果同步", value: "24 小时内" },
];

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页" },
    { "@type": "ListItem", position: 2, name: "举报通道" },
  ],
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "直播举报通道",
  description:
    "爆料直播举报通道，支持画面中断反馈、主播违规投诉、消费与打赏争议、版权与素材争议的在线提交，24 小时内完成初步受理。",
  inLanguage: "zh-CN",
  about: ["直播举报通道", "主播违规投诉", "画面中断反馈"],
};

export default function Report() {
  usePageMeta({
    title: "直播举报通道 - 画面中断反馈与主播违规投诉 | 爆料直播",
    description:
      "爆料直播举报通道支持在线提交画面中断反馈、主播违规投诉、消费与打赏争议、版权与素材争议，24 小时内完成初步受理，生成受理编号后可全程跟踪核查进度与平台处理结果。",
    keywords: "直播举报通道,画面中断反馈,主播违规投诉,直播争议事件,平台处理结果",
    path: "/report",
  });

  return (
    <SiteLayout>
      <JsonLd id="ld-report-webpage" data={webPageSchema} />
      <JsonLd id="ld-report-breadcrumb" data={breadcrumbSchema} />

      <PageHeader
        eyebrow="举报通道"
        title="直播举报通道：画面中断、主播违规一站直达"
        description="填写下方表单即可提交直播争议线索。平台在 24 小时内完成初步受理并生成受理编号，核查进度与平台处理结果都会通过你填写的邮箱同步。"
        crumbs={[{ label: "首页", path: "/" }, { label: "举报通道" }]}
        meta={
          <div className="flex flex-wrap gap-3">
            {timingChips.map((chip) => (
              <span
                key={chip.label}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700"
              >
                <i className={`${chip.icon} text-primary-500`} aria-hidden="true"></i>
                {chip.label}
                <strong className="font-heading text-foreground-950">{chip.value}</strong>
              </span>
            ))}
          </div>
        }
      />

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            <ReportForm />
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">受理范围</h2>
              <ul className="mt-4 space-y-4">
                {reportScopes.map((scope) => (
                  <li key={scope.title} className="flex gap-3">
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-background-50 text-primary-600 border border-background-200">
                      <i className={`${scope.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">{scope.title}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-foreground-600">{scope.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-accent-50 border border-accent-200 rounded-lg p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-accent-950">隐私与保密</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                举报人信息严格保密，未获授权不会向被举报方披露。进入处理结果公示区的内容仅保留事件概述与核查结论，个人敏感信息会被隐去。
              </p>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                如需撤回线索或补充材料，可通过受理编号与联系邮箱发起申请。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100 border-y border-background-200">
        <SectionHeading
          eyebrow="流程说明"
          title="你的线索会经历这四个环节"
          description="每个环节都有明确的责任方与时间要求，提交后可使用受理编号随时查询走到哪一步。"
        />
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {reportSteps.map((item) => (
            <li key={item.step} className="bg-background-50 border border-background-200 rounded-lg p-5">
              <span className="font-heading text-3xl font-bold text-primary-500">{item.step}</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <SectionHeading
          eyebrow="举报须知"
          title="提交之前，请先确认这几件事"
          description="遵守这些约定能让你的线索更快进入核查流程，也能让公示内容经得起回看。"
        />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {reportNotes.map((note, index) => (
            <div key={note.title} className="bg-background-100 border border-background-200 rounded-lg p-5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary-100 text-primary-700 font-heading text-sm font-bold">
                {index + 1}
              </span>
              <h3 className="mt-3 text-base font-medium text-foreground-950">{note.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{note.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}